import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

function iso(d) { return d.toISOString().slice(0, 10); }
function parseIso(s) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }
function fmt(d) { return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function addYears(d, n) { const x = new Date(d); x.setFullYear(x.getFullYear() + n); return x; }

// Compute comparison range from primary range + mode
export function computeComparison(primaryStartISO, primaryEndISO, mode) {
  if (!primaryStartISO || !primaryEndISO || mode === "none") return null;
  const s = parseIso(primaryStartISO);
  const e = parseIso(primaryEndISO);
  if (mode === "same_dates") {
    // Literal previous calendar year (may fall on different weekday)
    return { start: iso(addYears(s, -1)), end: iso(addYears(e, -1)) };
  }
  if (mode === "same_period") {
    // 52 weeks back so days-of-week align
    return { start: iso(addDays(s, -364)), end: iso(addDays(e, -364)) };
  }
  // fallback: immediately prior same-length window
  const days = Math.round((e - s) / 86400000) + 1;
  const ce = addDays(s, -1);
  return { start: iso(addDays(ce, -(days - 1))), end: iso(ce) };
}

const OPTIONS = [
  { key: "none", label: "No comparison" },
  { key: "same_dates", label: "Same dates in previous year" },
  { key: "same_period", label: "Same period in previous year", sub: "Matching to weekdays" },
];

export default function ComparisonPicker({ primaryStart, primaryEnd, mode, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const [pendingMode, setPendingMode] = useState(mode || "same_period");
  useEffect(() => { setPendingMode(mode || "same_period"); }, [mode]);

  const range = computeComparison(primaryStart, primaryEnd, pendingMode);
  const currentRange = computeComparison(primaryStart, primaryEnd, mode);
  const buttonLabel = mode === "none"
    ? "No comparison"
    : currentRange
      ? `${fmt(parseIso(currentRange.start))} to ${fmt(parseIso(currentRange.end))}`
      : "—";

  function apply() {
    onChange?.({ mode: pendingMode, range });
    setOpen(false);
  }

  return (
    <div className="relative" ref={ref} data-testid="comparison-picker">
      <div className="bm-eyebrow mb-2 text-neutral-500">Comparison</div>
      <button
        data-testid="comparison-trigger"
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-2 border border-dashed border-[#0a0a0a]/70 font-mono-bm text-sm flex items-center gap-2 hover:bg-[#0a0a0a]/5 text-neutral-700"
      >
        {buttonLabel}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-testid="comparison-popover" className="absolute z-50 mt-2 w-[340px] bg-[#f5f2eb] border border-[#0a0a0a] shadow-[8px_8px_0_0_#0a0a0a] right-0 bm-popover-mobile">
          <div className="p-5 space-y-4">
            {OPTIONS.map((opt) => {
              const optRange = computeComparison(primaryStart, primaryEnd, opt.key);
              return (
                <div key={opt.key}>
                  <button
                    type="button"
                    data-testid={`compare-${opt.key}`}
                    onClick={() => setPendingMode(opt.key)}
                    className="flex items-start gap-3 w-full text-left"
                  >
                    <span className={`w-4 h-4 mt-0.5 rounded-full border-2 border-[#0a0a0a] flex items-center justify-center flex-shrink-0 ${pendingMode === opt.key ? "bg-[#c72c2c] border-[#c72c2c]" : ""}`}>
                      {pendingMode === opt.key && <span className="w-1.5 h-1.5 rounded-full bg-[#f5f2eb]" />}
                    </span>
                    <span>
                      <span className="text-sm block">{opt.label}</span>
                      {opt.sub && <span className="bm-eyebrow text-neutral-500 mt-0.5 block">{opt.sub}</span>}
                      {optRange && opt.key !== "none" && (
                        <span className="bm-eyebrow text-neutral-500 mt-0.5 block">
                          {fmt(parseIso(optRange.start))} to {fmt(parseIso(optRange.end))}
                        </span>
                      )}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
          <div className="border-t border-[#0a0a0a] px-5 py-3 flex items-center justify-between gap-3">
            <div className="font-mono-bm text-xs text-neutral-700 truncate">
              {pendingMode === "none" ? "No comparison" : range && `${fmt(parseIso(range.start))} to ${fmt(parseIso(range.end))}`}
            </div>
            <button
              data-testid="comparison-apply"
              onClick={apply}
              className="bm-eyebrow px-4 py-2 bg-[#0a0a0a] text-[#f5f2eb] hover:bg-[#c72c2c]"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
